/**
 * O que faz: Renderiza gráficos estáticos e previsíveis para o documento exportável.
 * Onde usa: Páginas analíticas do DashboardReportDocument.
 * Conexões: DashboardReportModel e dados agregados do dashboard.
 * Tabelas: Nenhuma; consome métricas já calculadas em memória.
 * Data: 27/07/2026.
 */

import type { ReactNode } from 'react';
import type { DashboardReportModel } from '@/features/dashboard/report/buildDashboardReportModel';

interface ReportSectionCardProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
}

export const ReportSectionCard = ({ title, subtitle, children, className = '' }: ReportSectionCardProps) => (
  // Não usa sombra: html2canvas pode rasterizar sombras grandes como blocos opacos no PDF.
  <section className={`rounded-[24px] border border-[#E0E9D8] bg-white p-7 ${className}`}>
    <h2 className="text-[29px] tracking-[-0.03em] text-[#173324]" style={{ fontFamily: 'Sora, Arial, sans-serif', fontWeight: 700 }}>
      {title}
    </h2>
    {subtitle && <p className="mt-2 text-[19px] leading-7 text-[#68766A]">{subtitle}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

const chartColor = { ads: '#4E8614', links: '#A2D83D', other: '#AAB9AD' };
const chartLabel: Record<string, string> = { ads: 'Anúncios', links: 'Links', other: 'Outros' };

const buildPoints = (values: number[], maximum: number, compact: boolean) => values.map((value, index) => {
  const chartTop = compact ? 56 : 70;
  const chartBottom = compact ? 266 : 850;
  const x = values.length <= 1 ? 525 : 100 + (index / (values.length - 1)) * 850;
  const y = maximum === 0 ? chartBottom : chartBottom - (value / maximum) * (chartBottom - chartTop);
  return `${x},${y}`;
}).join(' ');

export const DailyOriginChart = ({ model, compact = false, balanced = false }: { model: DashboardReportModel; compact?: boolean; balanced?: boolean }) => {
  const series = model.dailySeries;
  const maximum = Math.max(1, ...series.map((item) => item.total));
  const hasData = series.some((item) => item.total > 0);
  const chartTop = compact ? 56 : balanced ? 56 : 70;
  const chartBottom = compact ? 266 : balanced ? 430 : 850;
  const chartViewHeight = compact ? 320 : balanced ? 500 : 1000;
  const chartClass = compact ? 'h-[310px]' : balanced ? 'h-[460px]' : 'h-[1500px]';
  const guides = [0, 1, 2, 3].map((index) => {
    const progress = index / 3;
    return { y: chartTop + (chartBottom - chartTop) * progress, label: Math.round(maximum * (1 - progress)) };
  });

  if (!hasData) {
    return <div className={`flex ${compact ? 'h-[300px]' : balanced ? 'h-[460px]' : 'h-[1500px]'} items-center justify-center rounded-[20px] border border-[#E0E9D8] bg-[#F6F9F1] text-[20px] text-[#6B7B6C]`}>Nenhum contato no período selecionado.</div>;
  }

  return (
    <div>
      <svg viewBox={`0 0 1000 ${chartViewHeight}`} preserveAspectRatio="none" className={`${chartClass} w-full`} role="img" aria-label="Evolução diária de contatos por origem">
        {guides.map(({ y, label }) => <g key={y}><line x1="100" x2="950" y1={y} y2={y} stroke="#E7EDE1" strokeWidth="2" /><text x="82" y={y + 7} textAnchor="end" fill="#6B7B6C" fontSize={compact ? '17' : '23'} fontFamily="DM Sans, Arial">{label}</text></g>)}
        <polyline fill="none" points={buildPoints(series.map((item) => item.other), maximum, compact)} stroke={chartColor.other} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
        <polyline fill="none" points={buildPoints(series.map((item) => item.links), maximum, compact)} stroke={chartColor.links} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
        <polyline fill="none" points={buildPoints(series.map((item) => item.ads), maximum, compact)} stroke={chartColor.ads} strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
        {series.filter((_, index) => index === 0 || index === series.length - 1 || index === Math.floor(series.length / 2)).map((item, index, visible) => {
          const originalIndex = series.indexOf(item);
          const x = visible.length <= 1 ? 525 : 100 + (originalIndex / (series.length - 1)) * 850;
          return <text key={`${item.dateKey}-${index}`} x={x} y={compact ? '307' : balanced ? '475' : '925'} textAnchor="middle" fill="#6B7B6C" fontSize={compact ? '19' : '23'} fontFamily="DM Sans, Arial">{item.label}</text>;
        })}
      </svg>
      <div className="mt-3 flex flex-wrap gap-5 text-[18px] text-[#526255]">
        {Object.entries(chartColor).map(([key, color]) => <span key={key} className="inline-flex items-center gap-2"><i className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />{chartLabel[key]}</span>)}
      </div>
    </div>
  );
};

export const WeekdayChart = ({ model, compact = false, balanced = false }: { model: DashboardReportModel; compact?: boolean; balanced?: boolean }) => {
  const maximum = Math.max(1, ...model.weekdaySeries.map((item) => item.leads));
  const best = model.weekdaySeries.reduce((currentBest, item) => item.leads > currentBest.leads ? item : currentBest, model.weekdaySeries[0]);

  return (
    <div>
      <div className={`grid ${compact ? 'h-[290px]' : balanced ? 'h-[420px]' : 'h-[1350px]'} grid-cols-7 items-end gap-3 border-b border-[#DEE8D5] px-2`}>
        {model.weekdaySeries.map((item) => <div key={item.label} className="flex h-full flex-col justify-end text-center">
          <span className="mb-3 text-[18px] font-bold text-[#355F1A]">{item.leads}</span>
          <div className="min-h-[8px] rounded-t-[18px]" style={{ height: `${Math.max(3, (item.leads / maximum) * 100)}%`, background: item.label === best.label && item.leads > 0 ? '#4E8614' : '#B8F33B' }} />
          <span className="mt-3 text-[17px] text-[#68766A]">{item.shortLabel}</span>
        </div>)}
      </div>
      <p className="mt-6 rounded-full bg-[#F3F8EC] px-5 py-3 text-[19px] text-[#355F1A]">Melhor dia: <strong>{best.leads > 0 ? `${best.label} (${best.percentage}% dos contatos)` : 'sem dados no período'}</strong></p>
    </div>
  );
};

export const OriginConversionChart = ({ model }: { model: DashboardReportModel }) => {
  const origins = [
    ['Anúncios', model.originConversion.anuncios, '#4E8614'],
    ['Links exclusivos', model.originConversion.links, '#A2D83D'],
    ['Outros', model.originConversion.outros, '#AAB9AD'],
  ] as const;

  return (
    <div className="space-y-5">
      {origins.map(([label, metric, color]) => (
        <div key={label}>
          <div className="flex items-baseline justify-between gap-4 text-[20px]">
            <span className="font-bold text-[#173324]">{label}</span>
            <span className="text-[#526255]">{metric.convertidos}/{metric.contatos} <strong className="text-[#355F1A]">({metric.taxa.toLocaleString('pt-BR')}%)</strong></span>
          </div>
          <div className="mt-2 h-4 overflow-hidden rounded-full bg-[#EAF0E5]">
            <div className="h-full rounded-full" style={{ width: `${Math.min(metric.taxa, 100)}%`, backgroundColor: color }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const ResponseTimeSummary = ({ model }: { model: DashboardReportModel }) => {
  const distribution = model.responseTime.distribution;
  const totalResponses = distribution ? Object.values(distribution).reduce((total, value) => total + value, 0) : 0;
  const ranges = distribution ? [
    ['Até 30 min', distribution.ate30min, '#76B82A'],
    ['30 min – 1h', distribution.de30mina1h, '#A2D83D'],
    ['1h – 4h', distribution.de1ha4h, '#F0BF48'],
    ['Mais de 4h', distribution.acimaDe4h, '#E2785F'],
  ] as const : [];

  return (
    <div>
      <div className="grid grid-cols-2 gap-4">
        {[
          ['Geral', model.responseTime.overall],
          ['Humano', model.responseTime.human],
          ['IA', model.responseTime.ai],
          ['Comercial', model.responseTime.businessHours],
        ].map(([label, value]) => (
          <div key={label} className="rounded-[20px] bg-[#F4F8ED] px-5 py-4">
            <p className="text-[16px] font-bold uppercase tracking-[0.08em] text-[#5D7A3B]">{label}</p>
            <p className="mt-2 text-[29px] tracking-[-0.04em] text-[#173324]" style={{ fontFamily: 'Sora, Arial, sans-serif', fontWeight: 700 }}>{value}</p>
          </div>
        ))}
      </div>
      {distribution ? (
        <div className="mt-6 space-y-3">
          {ranges.map(([label, value, color]) => (
            <div key={label} className="flex items-center gap-3 text-[18px]">
              <span className="w-[130px] text-[#617164]">{label}</span>
              <div className="h-3 flex-1 overflow-hidden rounded-full bg-[#EAF0E5]">
                <div className="h-full rounded-full" style={{ width: `${totalResponses ? (value / totalResponses) * 100 : 0}%`, backgroundColor: color }} />
              </div>
              <strong className="w-8 text-right text-[#173324]">{value}</strong>
            </div>
          ))}
        </div>
      ) : <p className="mt-6 text-[19px] text-[#6B7B6C]">Ainda não há mensagens suficientes para calcular o tempo de resposta.</p>}
    </div>
  );
};
